import { DndContext, rectIntersection } from "@dnd-kit/core";
import { Box, Dialog, Paper, useTheme } from "@mui/material";
import { globalMessageHandler, useDialog } from "common";
import CalendarPro from "common/components/CalendarPro/CalendarPro";
import BoatCheckboxSelector from "components/BoatCheckboxSelector/BoatCheckboxSelector";
import ProgramCheckboxSelector from "components/ProgramCheckboxSelector/ProgramCheckboxSelector";
import EventGroupForm from "features/event-groups/EventGroupForm";
import { EventGroupItem } from "features/event-groups/eventGroupApi";
import { EventEditorProvider } from "features/events/Editor/EventEditorContext";
import { dragDropUpdate } from "features/events/Editor/eventEditorApi";
import I18n from "i18n";
import moment from "moment";
import { useRef } from "react";
import CalendarToolbar from "./CalendarToolbar";
import EditRowHeader from "./EditRowHeader";
import EventCalendarModals from "./EventCalendarModals";
import EventDragOverlay from "./EventDragOverlay";
import EventToolbar from "./EventToolbar";
import CalendarCellHeaderSmall from "./cells/CalendarCellHeaderSmall";
import StartBoatCell from "./cells/StartBoatCell";
import useEventCalendar from "./useEventCalendar";

export default function EventCalendar() {
  const theme = useTheme();
  const {
    boatIds,
    setBoatIds,
    items,
    event,
    setEvent,
    eventDialog,
    booking,
    setBooking,
    bookingDialog,
    loadEvents,
    cal,
    programIds,
    setProgramIds,
    settings,
    setSettings,
    initialDate,
    initialView,
  } = useEventCalendar({ filterKey: "event", filterStorageType: "session" });

  const [newEvent, setNewEvent, newDialogProps] = useDialog<
    Partial<EventGroupItem> & { event_id: number }
  >();
  const overlayRef = useRef<any>();

  function handleDragStart(event: any) {
    if (overlayRef.current) {
      overlayRef.current.setActiveEvent(event.active.data.current);
    }
  }

  async function handleDragEnd({ over, active, delta }: any) {
    if (Math.abs(delta?.x || 0) + Math.abs(delta?.y || 0) < 10) {
      if (overlayRef.current) {
        overlayRef.current.setActiveEvent(null);
      }
      return;
    }
    if (!active?.id) {
      if (overlayRef.current) {
        overlayRef.current.setActiveEvent(null);
      }
      return;
    }
    const sourceEvent = active.data?.current;
    if (!over?.id) {
      setNewEvent({
        start_time: sourceEvent?.start_time,
        duration: sourceEvent?.duration,
        number_of_places: sourceEvent?.number_of_places,
        places_tolerance: sourceEvent?.places_tolerance,
        number_of_online_places: sourceEvent?.number_of_online_places,
        boat_id: sourceEvent?.boat_id,
        event_id: sourceEvent?.id,
        boat: sourceEvent?.boat,
        program: sourceEvent?.program,
      } as any);
      return;
    }

    const targetGroup = over.data?.current;

    if (sourceEvent?.event_group_id === over?.id) {
      return;
    }

    // Build a translated confirmation message using placeholders.
    const confirmMessage = I18n.t("EventEditor.dragDropConfirmMessage", {
      program: sourceEvent.program.name,
      sourceBoat: sourceEvent.boat.name,
      sourceTime: moment(sourceEvent.start_time).format("L LT"),
      targetBoat: targetGroup.boat.name,
      targetTime: moment(targetGroup?.events[0].start_time).format("L LT"),
    });

    globalMessageHandler.confirm(
      {
        title: I18n.t("EventEditor.dragDropConfirmTitle"),
        message: confirmMessage,
      },
      async () => {
        const { success, data, error } = await dragDropUpdate({
          event_id: active?.id,
          new_event_group_id: over?.id,
        });
        if (success) {
          cal.current?.refresh();
        }
      }
    );

    // Clear the active dragged event
    if (overlayRef.current) {
      overlayRef.current.setActiveEvent(null);
    }
  }

  return (
    <DndContext
      //collisionDetection={closestCenter}
      collisionDetection={rectIntersection}
      //onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Paper
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          //overflow: "hidden",
        }}
      >
        <EventEditorProvider items={items} cal={cal}>
          <CalendarPro
            useDynamicBox
            calRef={cal}
            navigateDelay={300}
            autoLoad
            loadEvents={loadEvents}
            ToolbarComponent={CalendarToolbar}
            headerContent={
              <EventToolbar
                setBooking={setBooking}
                setBoatIds={setBoatIds}
                boatIds={boatIds}
              />
            }
            customToolbar={
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: settings.compact_filters ? "row" : "column",
                  alignItems: settings.compact_filters
                    ? "space-between"
                    : "center",
                  px: 1,
                  gap: 2,
                  "& label": {
                    fontWeight: 500,
                  },
                }}
              >
                <BoatCheckboxSelector
                  value={boatIds}
                  color={theme.palette.primary.main}
                  onChange={evt => {
                    setBoatIds(evt.target.value || []);
                  }}
                  compact={!!settings.compact_filters}
                  sx={{ justifyContent: "flex-start" }}
                />
                <ProgramCheckboxSelector
                  value={programIds}
                  onChange={evt => {
                    setProgramIds(evt.target.value || []);
                  }}
                  showMore={settings.show_all_programs}
                  setShowMore={show_all =>
                    setSettings({ show_all_programs: show_all })
                  }
                  compact={!!settings.compact_filters}
                />
              </Box>
            }
            view={initialView.current}
            events={items}
            date={initialDate.current}
            containerStyle={{
              flexDirection: "column",
            }}
            CellComponent={StartBoatCell}
            CellHeaderComponent={CalendarCellHeaderSmall}
            renderRowHeaderAction={({ time }) => <EditRowHeader time={time} />}
            /*renderTableHeaderCellAction={({ date, label }) => (
              <EditColHeader date={date} label={label} />
            )}*/
          />
          <EventDragOverlay ref={overlayRef} />
        </EventEditorProvider>
        <EventCalendarModals cal={cal} />
        <Dialog {...newDialogProps} maxWidth="xl" fullWidth>
          <EventGroupForm
            id={"create"}
            eventToMove={newEvent as any}
            defaultValues={newEvent}
            onClose={newDialogProps.onClose}
            afterSave={() => {
              cal?.current?.refresh();
              newDialogProps.onClose &&
                newDialogProps.onClose({}, "backdropClick");
            }}
          />
        </Dialog>
      </Paper>
    </DndContext>
  );
}
